// src/Admin/ClockChange.js
/* =========================================================
   Admin › Settings › Lebanon clock change.
   Class times are Lebanon times. When Lebanon changes its clock, families
   abroad whose country doesn't change the same day see their class move by
   1 hour in their own time. For each of them:
     - WhatsApp the parents (message ready: keep their time, or take the new one)
     - "Keep their time": the classes (and weekly slots) move in Lebanon time
     - "Keep Lebanon time": nothing moves, only noted
     - Undo puts things back.
   API: GET /api/admin/clockchange, POST /api/admin/clockchange/apply
   ========================================================= */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClockRotateLeft,
  faTriangleExclamation,
  faCircleCheck,
  faRotateLeft,
  faArrowRight,
  faChalkboardUser,
  faChevronDown,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryFromTz } from './Studentzone';
import './ClockChange.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const digits = (p) => String(p || '').replace(/\D/g, '');
const waUrl = (phone, text) => `https://api.whatsapp.com/send/?phone=${digits(phone)}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;
const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || '';
const hoursText = (min) => {
  const h = Math.abs(min) / 60;
  return `${h} ${h === 1 ? 'hour' : 'hours'}`;
};
const utc = (h) => (h === null || h === undefined ? '' : `UTC${h < 0 ? '−' : '+'}${Math.abs(h)}`);
const unique = (list) => [...new Set(list)];

const Flag = ({ zone }) => {
  const code = countryFromTz(zone);
  return code ? <span className={`fi fi-${code} rmclk-flag`} aria-hidden="true" /> : <FontAwesomeIcon icon={faGlobe} className="rmclk-flag-icon" />;
};

const parentMessage = (s, change) => {
  const usual = unique(s.classes.map((c) => c.theirUsual)).join(' / ');
  const kept = unique(s.classes.map((c) => c.theirIfKept)).join(' / ');
  const back = change.shiftMin < 0 ? 'back' : 'forward';
  const name = firstName(s.name);
  return [
    'Hello! This is RoboMind Academy 👋',
    '',
    `On ${change.label.replace(/ \d{4}$/, '')}, Lebanon moves its clocks 1 hour ${back}.`,
    `${name}'s class is usually at ${usual} your time. After the change it would be at ${kept}${s.lasts ? '' : ' (only until your clocks change too)'}.`,
    '',
    'What do you prefer?',
    `1️⃣ Keep ${usual} your time (we adjust the class time)`,
    `2️⃣ Move to ${kept} your time`,
    '',
    'Just reply 1 or 2 🙏',
  ].join('\n');
};

const teacherMessage = (t, change) =>
  [
    `Hello ${firstName(t.name)}! 👋`,
    '',
    `On ${change.label.replace(/ \d{4}$/, '')}, Lebanon moves its clocks 1 hour ${change.shiftMin < 0 ? 'back' : 'forward'}.`,
    `Class times on the website stay in Lebanon time, so in ${t.city} your classes will be ${hoursText(t.shift)} ${t.shift < 0 ? 'later' : 'earlier'} than now${
      t.lasts ? '' : ' (until your clocks change too)'
    }.`,
    'Your dashboard already shows the right times. Tell us if any class doesn’t work for you 🙏',
  ].join('\n');

const ClockChange = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // student id being saved
  const [open, setOpen] = useState({}); // student id -> classes list open
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/clockchange`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Update the server to use this (new backend file).' : 'We couldn’t check the students abroad.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!note) return undefined;
    const t = setTimeout(() => setNote(''), 4500);
    return () => clearTimeout(t);
  }, [note]);

  const act = async (s, action, force = false) => {
    if (action === 'move' && !force) {
      const to = unique(s.classes.map((c) => c.lebanonTo.split(', ').pop())).join(' / ');
      if (!window.confirm(`Move ${s.name}'s ${s.classes.length} upcoming ${s.classes.length === 1 ? 'class' : 'classes'} ${hoursText(s.shift)} ${s.shift < 0 ? 'earlier' : 'later'} in Lebanon time (${to})?\nThe teacher gets a notification.`)) return;
    }
    if (action === 'undo' && !window.confirm(s.choice === 'moved' ? `Put ${s.name}'s classes back to the earlier Lebanon times?` : `Forget the choice for ${s.name}?`)) return;
    setBusy(s.id);
    try {
      const res = await axios.post(`${API}/api/admin/clockchange/apply`, { studentId: s.id, action, force }, auth());
      if (action === 'move') setNote(`${s.name}: ${res.data.moved} ${res.data.moved === 1 ? 'class' : 'classes'} moved${res.data.slots ? ` + ${res.data.slots} weekly ${res.data.slots === 1 ? 'slot' : 'slots'}` : ''}.`);
      if (action === 'keep') setNote(`${s.name}: stays on Lebanon time.`);
      if (action === 'undo') setNote(s.choice === 'moved' ? `${s.name}: ${res.data.undone} ${res.data.undone === 1 ? 'class' : 'classes'} back.` : `${s.name}: choice removed.`);
      await load();
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.clashes) {
        const list = [
          ...(d.clashes || []).map((c) => `• ${c.lebanonTo} clashes with ${c.clash.with}`),
          ...(d.slotClashes || []).map((sl) => `• weekly slot ${sl.to} is taken by ${sl.taken} (stays at ${sl.from})`),
        ].join('\n');
        if (window.confirm(`${list}\n\nMove anyway? You can fix the clashes in the student profiles after.`)) {
          setBusy(null);
          act(s, 'move', true);
          return;
        }
      } else {
        setNote(d?.error || 'Couldn’t save. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <section className="rmclk rmclk--muted">
        <p className="rmclk-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </p>
      </section>
    );
  }
  if (!data) return <div className="rmclk-skel" aria-hidden="true" />;
  if (!data.change) return null; // no clock change soon

  const { change, students, teachers } = data;
  const decided = students.filter((s) => s.choice).length;
  const back = change.shiftMin < 0;

  return (
    <section className="rmclk" aria-labelledby="rmclk-title">
      <div className="rmclk-head">
        <span className="rmclk-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faClockRotateLeft} />
        </span>
        <div className="rmclk-headtext">
          <span className="rmclk-eyebrow">Lebanon clock change</span>
          <h2 id="rmclk-title">
            {change.label} · clocks go {back ? 'back' : 'forward'} 1 hour
          </h2>
          <p>
            {change.past ? 'Lebanon changed' : 'Lebanon changes'} from {utc(change.before)} to {utc(change.after)}. Class times stay in Lebanon time, and the
            website updates its own time by itself. Families abroad whose clocks don’t change the same day see their class move by 1 hour: ask them what
            they prefer.
          </p>
        </div>
      </div>

      <ul className="rmclk-stats">
        <li className={students.length ? 'is-warn' : 'is-good'}>
          <strong>{students.length}</strong> {students.length === 1 ? 'family abroad' : 'families abroad'} affected
        </li>
        <li>
          <strong>{decided}</strong> decided
        </li>
        <li>
          <strong>{data.inLebanon}</strong> in Lebanon · no change
        </li>
        <li>
          <strong>{data.sameAsLebanon}</strong> abroad · same day change
        </li>
      </ul>

      {!data.tableReady && (
        <p className="rmclk-alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> Run <code>backend/sql/ClockChange.sql</code> on the database to save choices.
        </p>
      )}
      {data.noZone?.length > 0 && (
        <p className="rmclk-alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> Unknown time zone, check by hand: {data.noZone.join(', ')}
        </p>
      )}

      {students.length === 0 ? (
        <p className="rmclk-empty">
          <FontAwesomeIcon icon={faCircleCheck} /> No family abroad is affected.
        </p>
      ) : (
        <ul className="rmclk-list">
          {students.map((s) => {
            const usual = unique(s.classes.map((c) => c.theirUsual)).join(' / ');
            const kept = unique(s.classes.map((c) => c.theirIfKept)).join(' / ');
            const lebTo = unique(s.classes.map((c) => c.lebanonTo.split(', ').pop())).join(' / ');
            const isOpen = !!open[s.id];
            return (
              <li key={s.id} className={`rmclk-row ${s.choice ? 'is-done' : ''}`}>
                <div className="rmclk-who">
                  <Flag zone={s.zone} />
                  <div>
                    <Link to={`/Admin/studentprofile/${s.id}`} className="rmclk-name">
                      {s.name}
                    </Link>
                    <span className="rmclk-sub">
                      {s.city}
                      {s.teachers?.length > 0 && (
                        <>
                          {' · '}
                          <FontAwesomeIcon icon={faChalkboardUser} /> {s.teachers.map((t) => t.name).join(', ')}
                        </>
                      )}
                    </span>
                  </div>
                  {s.choice === 'moved' && <span className="rmclk-pill tone-green">Keeps their time ✓</span>}
                  {s.choice === 'kept' && <span className="rmclk-pill tone-blue">Lebanon time ✓</span>}
                  {!s.choice && s.clashes > 0 && <span className="rmclk-pill tone-red">{s.clashes} clash{s.clashes === 1 ? '' : 'es'}</span>}
                </div>

                {s.choice === 'moved' ? (
                  <p className="rmclk-line">
                    {(s.moved?.classes || []).length} {(s.moved?.classes || []).length === 1 ? 'class' : 'classes'} moved, still at{' '}
                    <b>{unique((s.moved?.classes || []).map((c) => c.theirUsual)).join(' / ') || 'their usual time'}</b> for them.
                    {(s.moved?.slots || []).length > 0 && ` Weekly: ${s.moved.slots.map((x) => `${x.from} → ${x.to}`).join(', ')}.`}
                    {(s.moved?.slotsNotMoved || []).length > 0 && (
                      <span className="rmclk-warn"> Weekly slot not moved: {s.moved.slotsNotMoved.map((x) => `${x.from} (${x.why})`).join(', ')}: fix it in the Slots tab.</span>
                    )}
                  </p>
                ) : (
                  <>
                    <p className="rmclk-line">
                      Usually <b>{usual}</b> for them <FontAwesomeIcon icon={faArrowRight} /> <b className="rmclk-hot">{kept}</b> after the change
                      {!s.lasts && <span className="rmclk-dim"> (one week only, then their clocks change too)</span>}.
                    </p>
                    <p className="rmclk-line rmclk-dim">
                      To keep {usual}: Lebanon time {lebTo} · {s.classes.length} {s.classes.length === 1 ? 'class' : 'classes'} after the change
                      {s.slots?.length > 0 &&
                        ` · weekly ${s.slots.map((x) => (x.to ? `${x.from} → ${x.to}${x.taken ? ` (taken by ${x.taken})` : ''}` : `${x.from} (no slot an hour ${s.shift < 0 ? 'earlier' : 'later'})`)).join(', ')}`}
                    </p>
                  </>
                )}

                {s.classes.length > 0 && !s.choice && (
                  <>
                    <button type="button" className="rmclk-more" aria-expanded={isOpen} onClick={() => setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}>
                      <FontAwesomeIcon icon={faChevronDown} /> {isOpen ? 'Hide classes' : 'See classes'}
                    </button>
                    {isOpen && (
                      <ul className="rmclk-classes">
                        {s.classes.map((c) => (
                          <li key={c.id} className={c.clash ? 'is-clash' : ''}>
                            <span>{c.lebanonNow}</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                            <span>
                              <b>{c.lebanonTo.split(', ').pop()}</b> Lebanon
                              {c.teacherTo && <span className="rmclk-dim"> · teacher {c.teacherTo}</span>}
                            </span>
                            {c.clash && (
                              <span className="rmclk-warn">
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {c.clash.with} at {c.clash.at.split(', ').pop()}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}

                <div className="rmclk-actions">
                  {s.mobile && (
                    <a
                      className="rmclk-btn rmclk-btn--wa"
                      href={waUrl(s.mobile, s.choice === 'moved' ? '' : parentMessage(s, change))}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FontAwesomeIcon icon={faWhatsapp} /> Ask parents
                    </a>
                  )}
                  {!s.choice && data.tableReady && (
                    <>
                      <button type="button" className="rmclk-btn" disabled={busy === s.id} onClick={() => act(s, 'move')}>
                        Keep {usual} (move class)
                      </button>
                      <button type="button" className="rmclk-btn rmclk-btn--ghost" disabled={busy === s.id} onClick={() => act(s, 'keep')}>
                        Take {kept} (no change)
                      </button>
                    </>
                  )}
                  {s.choice && (
                    <button type="button" className="rmclk-btn rmclk-btn--ghost" disabled={busy === s.id} onClick={() => act(s, 'undo')}>
                      <FontAwesomeIcon icon={faRotateLeft} /> Undo
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {teachers.length > 0 && (
        <div className="rmclk-teachers">
          <h3>
            <FontAwesomeIcon icon={faChalkboardUser} /> Teachers abroad
          </h3>
          <ul>
            {teachers.map((t) => (
              <li key={t.id}>
                <Flag zone={t.zone} />
                <span className="rmclk-tname">
                  <Link to={`/Admin/teacherprofile/${t.id}`}>{t.name}</Link>
                  <span className="rmclk-sub">
                    {t.city} · {t.classes} {t.classes === 1 ? 'class' : 'classes'} {hoursText(t.shift)} {t.shift < 0 ? 'later' : 'earlier'} for them
                    {!t.lasts && ' (for a week)'}
                  </span>
                </span>
                {t.mobile && (
                  <a className="rmclk-btn rmclk-btn--wa rmclk-btn--sm" href={waUrl(t.mobile, teacherMessage(t, change))} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faWhatsapp} /> Tell
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {note && (
        <div className="rmclk-toast" role="status">
          {note}
        </div>
      )}
    </section>
  );
};

export default ClockChange;
